import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  SetStateAction,
  Dispatch,
  ReactNode,
} from "react";
import classNames from "classnames";
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
  RichUtils,
  EditorCommand,
  Modifier,
  SelectionState,
} from "draft-js";
import Editor from "@draft-js-plugins/editor";
import createMentionPlugin, {
  type MentionData,
} from "@draft-js-plugins/mention";
import createEmojiPlugin, { PopperOptions } from "@draft-js-plugins/emoji";
import createLinkifyPlugin from "@draft-js-plugins/linkify";
import createStaticToolbarPlugin from "@draft-js-plugins/static-toolbar";
import createLinkPlugin from "./createLinkPlugin";
import { useSelector } from "react-redux";
import htmlToDraft from "html-to-draftjs";
import {
  BoldButton,
  ItalicButton,
  UnderlineButton,
} from "@draft-js-plugins/buttons";

import useOnClickOutside from "src/app/methods/useOnClickOutside";
import { RootState } from "src/redux/reducers";
import { ReactComponent as VariableIcon } from "src/images/variable.svg";
import { ReactComponent as AtSign } from "../../../images/at-sign.svg";
import { ReactComponent as EmojiIcon } from "../../../images/emoji.svg";
import { extractEmails, setCaretPosition } from "../../../utils/methods";
import mentionsStyles from "./utils/MentionStyles.module.scss";
import variablesStyles from "./utils/VariableStyles.module.scss";
import emojiStyles from "./utils/EmojiStyles.module.scss";
import buttonStyles from "./utils/ButtonStyles.module.scss";
import toolbarStyles from "./utils/ToolbarStyles.module.scss";
import {
  getMentionsWithPositions,
  getTextWithEncodedMentions,
  replaceHTMLTagsWithSpan,
} from "./functions";
import CustomMentionEntry from "./CustomMentionEntry";
import filterSuggestions from "./filterSuggestions";

import "./TextBox.scss";
import "draft-js/dist/Draft.css";

export interface EntityRanges {
  key: number;
  offset: number;
  length: number;
}

export interface TextBoxVariable {
  id: string;
  name: string;
}

interface AnchorPosition {
  key: number;
  offset: number;
}

interface Props {
  idhTextareaStyling?: boolean;
  id?: string;
  className?: string;
  value?: string;
  placeholder?: string;
  onFocus?: () => void;
  onClick?: () => void;
  onBlur?: (e?: any) => void;
  setEncodedValue?: (value: string) => void;
  showMentionList?: boolean;
  setShowMentionList?: Dispatch<SetStateAction<boolean>>;
  addAttachmentIcon?: ReactNode;
  emojisToBottom?: boolean;
  showButtons?: boolean;
  enableMentions?: boolean;
  variables?: TextBoxVariable[];
  handleUseOnClickOutside?: () => void;
  readOnly?: boolean;
  editorHeaderChildren?: ReactNode;
  editorBottomChildren?: ReactNode;
  bottomLeftButton?: ReactNode;
  bottomRightButton?: ReactNode;
  enableEmoji?: boolean;
  shouldDisableParentElementFocus?: boolean;
  customKeyBinding?: (e: any) => any;
  popperOptions?: PopperOptions;
  toolbarMode?: "basic" | "no-basic" | "enhanced";
}

export const TextBox = forwardRef<HTMLTextAreaElement, Props>((props, ref) => {
  const {
    idhTextareaStyling,
    id,
    className,
    value,
    placeholder,
    onFocus,
    onClick,
    onBlur,
    setEncodedValue,
    showMentionList,
    setShowMentionList,
    emojisToBottom,
    showButtons,
    enableMentions,
    variables = [],
    handleUseOnClickOutside,
    readOnly,
    editorHeaderChildren,
    editorBottomChildren,
    bottomLeftButton,
    bottomRightButton,
    enableEmoji,
    shouldDisableParentElementFocus,
    customKeyBinding,
    popperOptions,
    toolbarMode,
  } = props;

  const innerRef = useRef<Editor | null>(null);

  const [editorState, setEditorState] = useState(() =>
    EditorState.createWithContent(
      ContentState.createFromText(typeof value === "string" ? value : ""),
    ),
  );
  const [mentionsOpen, setMentionsOpen] = useState(false);
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [mentionsSuggestions, setMentionsSuggestions] = useState<MentionData[]>(
    [],
  );
  const [variablesSuggestions, setVariablesSuggestions] = useState<
    TextBoxVariable[]
  >([]);
  const [applyValue, setApplyValue] = useState(false);
  const [lastAnchorPosition, setLastAnchorPosition] =
    useState<AnchorPosition>();
  const [lastSelectionState, setLastSelectionState] =
    useState<SelectionState>();

  const previousValueRef = useRef<string | undefined>(undefined);

  // @ts-ignore
  useOnClickOutside(ref, () => {
    if (handleUseOnClickOutside) {
      handleUseOnClickOutside();
      if (innerRef.current) {
        innerRef.current.blur();
      }
    }
  });

  const [mentionPlugin] = useState(
    createMentionPlugin({
      entityMutability: "IMMUTABLE",
      theme: mentionsStyles,
      mentionPrefix: "@",
      mentionTrigger: "@",
      supportWhitespace: true,
      popperOptions,
    }),
  );
  const [variablePlugin] = useState(
    createMentionPlugin({
      entityMutability: "IMMUTABLE",
      theme: variablesStyles,
      mentionTrigger: "{{",
      mentionRegExp: "(.*?)",
      supportWhitespace: true,
      popperOptions,
    }),
  );
  const [emojiPlugin] = useState(
    createEmojiPlugin({
      theme: emojiStyles,
      // useNativeArt: true,
    }),
  );
  const [linkifyPlugin] = useState(
    createLinkifyPlugin({
      component(props) {
        const goToUrl = () => {
          window.open(props.href, "_blank", "noreferrer, noopener");
        };

        return (
          <a
            {...props}
            className="text-box__hyperlink"
            rel="noopener noreferrer nofollow"
            target="_blank"
            onClick={goToUrl}
          />
        );
      },
      target: "_blank",
      rel: "noopener noreferrer nofollow",
    }),
  );
  const staticToolbarPlugin = createStaticToolbarPlugin({
    theme: { buttonStyles, toolbarStyles },
  });
  const [customLinkPlugin] = useState(createLinkPlugin());

  const plugins = [
    ...(enableMentions ? [mentionPlugin] : []),
    ...(enableEmoji ? [emojiPlugin] : []),
    ...(variables.length > 0 ? [variablePlugin] : []),
    ...(toolbarMode && toolbarMode !== "no-basic" ? [staticToolbarPlugin] : []),
    customLinkPlugin, // Handle LINK entities (uppercase) from htmlToDraft
    linkifyPlugin, // Handle link entities (lowercase) - auto-detected URLs
  ];

  const { membersList } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  useEffect(() => {
    setApplyValue(true);
  }, []);

  useEffect(() => {
    // Only process if applyValue is true and value exists
    if (!applyValue || !value) {
      return;
    }

    // Skip if value hasn't changed (but allow initial load when previousValueRef is undefined)
    if (
      previousValueRef.current !== undefined &&
      previousValueRef.current === value
    ) {
      return;
    }

    previousValueRef.current = value;

    const newEditorState = EditorState.createWithContent(
      convertFromRaw(getMentionsWithPositions(value, membersList, variables)),
    );

    // Apply decorator from current editorState if available
    if (editorState.getDecorator()) {
      handleChange(
        EditorState.set(newEditorState, {
          decorator: editorState.getDecorator(),
        }),
      );
    } else {
      handleChange(newEditorState);
    }
  }, [value, applyValue, membersList, variables]);

  const handleChange = useCallback((_editorState) => {
    if (setEncodedValue) {
      const encodedValue = getTextWithEncodedMentions(_editorState);
      setEncodedValue(encodedValue);
    }

    setEditorState(_editorState);
  }, []);

  const handleMentionsOpenChange = useCallback((_open) => {
    setMentionsOpen(_open);
  }, []);

  const handleVariablesOpenChange = useCallback((_open) => {
    setVariablesOpen(_open);
  }, []);

  useEffect(() => {
    if (setShowMentionList) {
      handleMentionsOpenChange(true);
    }
  }, [showMentionList]);

  useEffect(() => {
    handleVariablesOpenChange(showMentionList);
  }, [showMentionList]);

  const handleMentionsSearchChange = useCallback(
    ({ value }) => {
      const mentions =
        membersList
          ?.filter((member) => {
            return !member.hideOnMembersList && member.status !== "removed";
          })
          .map((member) => {
            return {
              id: member.id,
              name: member.name,
              email: member.email,
              avatar: member.avatarUrl,
            };
          }) || [];

      setMentionsSuggestions(filterSuggestions(value, mentions));
    },
    [membersList],
  );

  const handleVariablesSearchChange = useCallback(({ value }) => {
    const formatValue = (v: string) => v.toLowerCase();

    setVariablesSuggestions(
      variables.filter((v) => formatValue(v.name).includes(formatValue(value))),
    );
  }, []);

  const handleKeyCommand = (command: EditorCommand, state: EditorState) => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      handleChange(newState);
      return "handled";
    }

    return "not-handled";
  };

  const getAnchorPosition = () => {
    const currentBlockKey = editorState.getSelection().getStartKey();
    const offset = editorState.getSelection().getAnchorOffset();
    const key = editorState
      .getCurrentContent()
      .getBlockMap()
      .keySeq()
      .findIndex((k) => k === currentBlockKey);

    return {
      offset,
      key,
    };
  };

  function split(str: string, index: number) {
    const result = [str.slice(0, index), str.slice(index)];

    return result;
  }

  const addAtSignToValue = async () => {
    if (!lastAnchorPosition) return;

    const { offset } = lastAnchorPosition;
    const { key } = lastAnchorPosition;
    let blockKey = null;
    let atCount = 0;
    let offsetDiff = 0;

    const { blocks } = convertToRaw(editorState.getCurrentContent());
    const { entityMap } = convertToRaw(editorState.getCurrentContent());

    const focusedBlock = blocks[key];
    const dividedText = split(focusedBlock.text, offset);
    let newText = "";

    const firstChar = dividedText[0][0];
    const lastChar = dividedText[0][dividedText[0].length - 1];

    if (lastChar === "@") {
      newText = focusedBlock.text;
    } else if (lastChar && lastChar !== " ") {
      newText = `${dividedText[0]} @${dividedText[1]}`;
      offsetDiff = 2;
    } else {
      newText = `${dividedText[0]}@${dividedText[1]}`;
      offsetDiff = 1;
    }

    if (lastChar !== "@") {
      atCount = dividedText[0]?.split("@").length;

      const emailsCount = extractEmails(dividedText[0])?.length;
      if (emailsCount) {
        atCount -= emailsCount;
      }
    }

    if (firstChar === "@") {
      atCount -= 1;
    }

    const newBlocks = blocks.map((item) => {
      if (focusedBlock.key === item.key) {
        blockKey = item.key;

        const newEntityRanges = item.entityRanges.map((range) => {
          if (range.offset > offset) {
            return {
              ...range,
              offset: range.offset + offsetDiff,
            };
          }
          return range;
        });

        atCount += newEntityRanges.length;

        return {
          ...item,
          text: newText,
          entityRanges: newEntityRanges,
        };
      }
      return item;
    });

    const newState = EditorState.createWithContent(
      convertFromRaw({
        blocks: newBlocks,
        entityMap,
      }),
    );

    await handleChange(newState);

    if (blockKey && atCount) {
      const blockContainer = document.querySelector(
        `[data-offset-key="${blockKey}-${atCount}-0"]`,
      );

      if (blockContainer) {
        setCaretPosition(blockContainer, 1);
      } else {
        if (innerRef.current) {
          innerRef.current.focus();
        }
      }
    }
  };

  const addBracketsToValue = async () => {
    if (!lastAnchorPosition) return;

    const { offset, key } = lastAnchorPosition;
    let blockKey = null;
    let bracketsCount = 0;
    let offsetDiff = 0;

    const { blocks, entityMap } = convertToRaw(editorState.getCurrentContent());

    const focusedBlock = blocks[key];
    const dividedText = split(focusedBlock.text, offset);
    let newText = "";

    const firstTwoChars = dividedText[0].substring(0, 2);
    const lastTwoChars = dividedText[0].slice(-2);

    if (lastTwoChars === "{{") {
      newText = focusedBlock.text;
    } else if (lastTwoChars && lastTwoChars !== " ") {
      newText = `${dividedText[0]} {{${dividedText[1]}`;
      offsetDiff = 3;
    } else {
      newText = `${dividedText[0]}{{${dividedText[1]}`;
      offsetDiff = 2;
    }

    if (firstTwoChars === "{{") {
      bracketsCount -= 1;
    }

    const newBlocks = blocks.map((item) => {
      if (focusedBlock.key === item.key) {
        blockKey = item.key;

        const newEntityRanges = item.entityRanges.map((range) => {
          if (range.offset > offset) {
            return {
              ...range,
              offset: range.offset + offsetDiff,
            };
          }
          return range;
        });

        bracketsCount += newEntityRanges.length;

        return {
          ...item,
          text: newText,
          entityRanges: newEntityRanges,
        };
      }
      return item;
    });

    const newState = EditorState.createWithContent(
      convertFromRaw({
        blocks: newBlocks,
        entityMap,
      }),
    );

    await handleChange(newState);

    if (blockKey) {
      const blockContainer = document.querySelector(
        `[data-offset-key="${blockKey}-${bracketsCount * 2 + 1}-0"]`,
      );

      if (blockContainer) {
        setCaretPosition(blockContainer, 2);
      } else {
        if (innerRef.current) {
          innerRef.current.focus();
        }
      }
    }
  };

  const renderBasicToolbarButtons = (externalProps: any) => {
    const props = {
      ...externalProps,
      getEditorState: () => editorState,
      setEditorState: handleChange,
    };

    return (
      <>
        <BoldButton {...props} />
        <ItalicButton {...props} />
        <UnderlineButton {...props} />
      </>
    );
  };

  const preserveSelection = () => {
    if (lastSelectionState) {
      handleChange(EditorState.forceSelection(editorState, lastSelectionState));
    }
  };

  const { MentionSuggestions } = mentionPlugin;
  const { MentionSuggestions: VariablesSuggestions } = variablePlugin;
  const { EmojiSuggestions, EmojiSelect } = emojiPlugin;
  const { Toolbar } = staticToolbarPlugin;

  return (
    <div
      id={id}
      className={classNames(
        "text-box",
        {
          "text-box--idh-styling": idhTextareaStyling,
          "text-box--emojis-to-bottom": emojisToBottom,
          "text-box--readOnly": readOnly,
        },
        className,
      )}
      onClick={() => {
        if (!shouldDisableParentElementFocus && innerRef.current) {
          innerRef.current.focus();
        }

        if (onClick) {
          onClick();
        }
      }}
    >
      <textarea
        className="event-trigger"
        ref={ref}
        onFocus={() => {
          if (innerRef.current) {
            innerRef.current.focus();
          }
        }}
      />
      {editorHeaderChildren}
      <Editor
        ref={innerRef}
        editorKey="editor"
        editorState={editorState}
        handleKeyCommand={handleKeyCommand}
        onChange={handleChange}
        plugins={plugins}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={() => {
          setLastAnchorPosition(getAnchorPosition());
          setLastSelectionState(editorState.getSelection());
          if (onBlur) {
            const encodedValue = getTextWithEncodedMentions(editorState);
            onBlur(encodedValue);
          }
        }}
        readOnly={readOnly}
        keyBindingFn={customKeyBinding}
        handlePastedText={(text: string, html: string | undefined) => {
          if (html) {
            const newHtml = replaceHTMLTagsWithSpan(html, ["ul", "ol", "li"]);

            const { contentBlocks, entityMap } = htmlToDraft(newHtml);

            const newContent = Modifier.replaceWithFragment(
              editorState.getCurrentContent(),
              editorState.getSelection(),
              ContentState.createFromBlockArray(
                contentBlocks,
                entityMap,
              ).getBlockMap(),
            );

            handleChange(
              EditorState.push(editorState, newContent, "insert-fragment"),
            );
            return "handled";
          }

          return "not-handled";
        }}
      />
      <EmojiSuggestions />
      {editorBottomChildren}
      {(showButtons || toolbarMode) && (
        <div className="text-box__buttons">
          <div className="text-box__buttons-left">
            {toolbarMode && (
              <div className="text-box__toolbar-wrapper">
                {toolbarMode !== "no-basic" && (
                  <Toolbar>
                    {(externalProps) =>
                      renderBasicToolbarButtons(externalProps)
                    }
                  </Toolbar>
                )}

                {toolbarMode === "enhanced" && (
                  <span className="text-box__toolbar-wrapper-separator" />
                )}

                {enableEmoji && (
                  <div className="text-box__buttons-left-emoji">
                    <EmojiIcon />
                    <EmojiSelect
                      closeOnEmojiSelect
                      onOpen={preserveSelection}
                    />
                  </div>
                )}

                {variables.length && (
                  <button
                    className="text-box__toolbar-wrapper-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      addBracketsToValue();
                    }}
                  >
                    <VariableIcon />
                  </button>
                )}
              </div>
            )}

            {enableMentions && (
              <AtSign
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  addAtSignToValue();
                }}
              />
            )}

            {bottomLeftButton}

            {enableEmoji && !toolbarMode && (
              <div className="text-box__buttons-left-emoji">
                <EmojiIcon />
                <EmojiSelect closeOnEmojiSelect onOpen={preserveSelection} />
              </div>
            )}
          </div>
          <div className="text-box__buttons-right">{bottomRightButton}</div>
        </div>
      )}

      {enableMentions && (
        <MentionSuggestions
          open={mentionsOpen}
          suggestions={mentionsSuggestions}
          onOpenChange={handleMentionsOpenChange}
          onSearchChange={handleMentionsSearchChange}
          entryComponent={CustomMentionEntry}
        />
      )}
      {variables.length > 0 && (
        <VariablesSuggestions
          open={variablesOpen}
          suggestions={variablesSuggestions}
          onOpenChange={handleVariablesOpenChange}
          onSearchChange={handleVariablesSearchChange}
        />
      )}
    </div>
  );
});
